import type { DomainMetadataDerivedFromUrl, NetworkEvent, RecordableEvent, HasWhenRecordedIso, HasEventIndex, RecordableEventCategory, RecordableEventName, UrlField, HasTab, RecordableEventNameWithinCategory } from "../../lib/Events";
import type { DataGoesWhereSettingsPartial, EvidenceRecordingSettings } from "../../lib/MultiWorld/DataGoesWhereSettings";
import type DTProto from "devtools-protocol";

export type ISODateTimeString = `${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`;
const isoDateTimeStringRegExp = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.[0-9]{3}Z$/;
export const isISODateTimeString = (candidate: unknown): candidate is ISODateTimeString =>
	typeof candidate === "string" && isoDateTimeStringRegExp.test(candidate);
export const nowAsISODateTimeString = () => new Date().toISOString() as ISODateTimeString;

export type UUID = ReturnType<typeof crypto.randomUUID>;
const uuidRegExp = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
export const isUUID = (candidate: unknown): candidate is UUID =>
	typeof candidate === "string" && uuidRegExp.test(candidate);

export interface StatisticsForNetworkRequests {
	whenFirstObservedIso: ISODateTimeString;
	whenLastObservedIso: ISODateTimeString;
	numberOfRequests: number;
}

export interface StatisticsForRequestsForUrl extends StatisticsForNetworkRequests {
	url: string;
}

export interface StatisticsForRequestsForDomain extends StatisticsForNetworkRequests {
	domain: string;
}
export interface StatisticsNodeForRequestsForDomainNode extends StatisticsForRequestsForDomain {
	urls: Map<string, StatisticsForRequestsForUrl>;
}

export interface StatisticsForRequestsForRegisteredDomain extends StatisticsForNetworkRequests {
	registeredDomain: string;
}

export interface StatisticsNodeForRequestsForRegisteredDomain extends StatisticsForRequestsForRegisteredDomain {
	domains: Map<string, StatisticsNodeForRequestsForDomainNode>;
}

export type NetworkOriginStatisticsTree = Map<string, StatisticsNodeForRequestsForRegisteredDomain>;

export interface RecordingNetworkStatisticsTree extends StatisticsForNetworkRequests {
	numberOfEvents: number;
	sourcePageOriginStatisticsTree: NetworkOriginStatisticsTree;
	destinationOriginStatisticsTree: NetworkOriginStatisticsTree;
}

export interface RecordingNetworkSummaryStatistics extends StatisticsForNetworkRequests {
	numberOfEvents: number;
	sourcePageRegisteredDomains: StatisticsForRequestsForRegisteredDomain[];
	destinationRegisteredDomains: StatisticsForRequestsForRegisteredDomain[];
}

export interface RecordingDataSummary extends StatisticsForNetworkRequests {
	numberOfEvents: number;
	sourcePageRegisteredDomains: StatisticsForRequestsForRegisteredDomain[];
	destinationRegisteredDomains: StatisticsForRequestsForRegisteredDomain[];
}

export interface HasRecordingUuid {
	recordingUuid: UUID;
}

export const RecordingStatusLiterals = {
	stopped: "stopped",
	archived: "archived",
	allTabs: "allTabs",
} as const;
export type RecordingStatusLiteral = typeof RecordingStatusLiterals[keyof typeof RecordingStatusLiterals];
export type RecordingStatus = RecordingStatusLiteral | number[];


export const isTabInRecording = ({tabId, recording}: {tabId: number, recording: DbRecording}) =>
	recording.status === RecordingStatusLiterals.allTabs ||
		(Array.isArray(recording.status) && recording.status.includes(tabId));

export const isRecordingInProgress = ({status: tabsRecordingInProgress}: DbRecording) =>
	tabsRecordingInProgress ===  RecordingStatusLiterals.allTabs ||	
		(Array.isArray(tabsRecordingInProgress) && tabsRecordingInProgress.length > 0);

export const isTabRecording = ({status: tabsRecordingInProgress}: {status: RecordingStatus | undefined}, tabId: number) =>
	tabsRecordingInProgress ===  RecordingStatusLiterals.allTabs ||	
		(Array.isArray(tabsRecordingInProgress) && tabsRecordingInProgress.includes(tabId));

export interface DbExtensionSettings {
	settingsIndex?: number;
	settings: DataGoesWhereSettingsPartial;
}

export type DbRecordedEventTabField<EVENT extends RecordableEvent = RecordableEvent> =
	EVENT extends {tab: UrlField} ? {readonly tab: EVENT["tab"] & DomainMetadataDerivedFromUrl} : {}

export interface NetworkEventAugmentedFields {
	readonly destination: NetworkEvent["destination"] & DomainMetadataDerivedFromUrl
	readonly initiatorOrigin?: string | undefined;
}

export type DbRecordedNetworkEventFields<EVENT extends RecordableEvent = RecordableEvent> =
	EVENT extends NetworkEvent ? NetworkEventAugmentedFields : {};


export interface FieldsNeededToRecordEvent extends HasRecordingUuid, HasEventIndex, HasWhenRecordedIso {}


export type AugmentedNetworkEvent = NetworkEvent & DomainMetadataDerivedFromUrl;

export type EventReadyToWriteToDb<
	EVENT extends RecordableEvent = RecordableEvent
> = EVENT & HasTab & DbRecordedNetworkEventFields<EVENT>;

export type DbRecordedEvent<
	EVENT extends RecordableEvent = RecordableEvent
> = EventReadyToWriteToDb<EVENT> & FieldsNeededToRecordEvent

export type RecordedEvent<EVENT extends RecordableEvent = RecordableEvent> =
	DbRecordedEvent<EVENT>; 

export type DbRecordedEventByCategory<
	EVENT_CATEGORY extends RecordableEventCategory
> = Extract<DbRecordedEvent, {category: EVENT_CATEGORY}>;

export type DbRecordedEventByCategoryAndName<
	EVENT_CATEGORY extends RecordableEventCategory,
	 EVENT_NAME extends RecordableEventName,
> = Extract<DbRecordedEvent, {category: EVENT_CATEGORY, eventName: EVENT_NAME}>;

export type DbRecordedEventByName<
	EVENT_NAME extends RecordableEventName,
> = Extract<DbRecordedEvent, {eventName: EVENT_NAME}>;


export type RecordedEventByCategoryAndName<
CATEGORY extends RecordableEventCategory,
NAME extends RecordableEventNameWithinCategory<CATEGORY>
> = Extract<RecordedEvent, { category: CATEGORY; eventName: NAME; }>;

export interface EventMetadataParameter {
	initiator?: string | DTProto.Network.Initiator | undefined;
}

export interface DbVideoFragment extends HasRecordingUuid {
	videoFragmentIndex: number;
	data: Blob;
	timecode: DOMHighResTimeStamp;
	streamId: string;
}
export type DbMhtmlCapture = /* chrome.webNavigation.WebNavigationFramedCallbackDetails  & */
HasRecordingUuid & {
	url: string;
	timeStamp: number;
	mhtmlCaptureIndex: number;
	mhtmlBlob: Blob;
};

export interface DbRecording extends HasRecordingUuid {
	startTimeIso: ISODateTimeString;
	name?: string | null;
	externallyVisibleIpAddress: string | undefined;
	status: RecordingStatus;
	searchTermsNewlineSeparated?: string | null;
	globalSearchTermsNewlineSeparated?: string | null;
	domGeneratedSearchTermsToIgnore?: string[] | null;
	/**
	 * Invalidate when dataSummary.numberOfEvents !== EventsTable.countForRecordingUuid(recordingUuid)
	 */
	dataSummary?: RecordingDataSummary | null;
	settings: EvidenceRecordingSettings;
}


export const eventsFileFormatVersion = 1;

export interface DataGoesWhereEventArchiveFormat extends Exclude<DbRecording, 'status'> {
	version: number;
	events: DbRecordedEvent[];
}


export type IndexForRecordingUuidThenNumericIndex = {
	'RecordingUuid-Index': [UUID, number];
}
export type NameOfIndexForRecordingUuidThenNumericIndex = keyof IndexForRecordingUuidThenNumericIndex;
export type TypesForIndexForRecordingUuidThenNumericIndex = IndexForRecordingUuidThenNumericIndex[NameOfIndexForRecordingUuidThenNumericIndex];
export const NameOfIndexForRecordingUuidThenNumericIndex = "RecordingUuid-Index" satisfies NameOfIndexForRecordingUuidThenNumericIndex;

export type IndexForRecordingUuidThenCategoryThenEventIndex = {
	"RecordingUuid-Category-EventIndex": [UUID, RecordableEventCategory, number];
}
export type NameOfIndexForRecordingUuidThenCategoryThenEventIndex = keyof IndexForRecordingUuidThenCategoryThenEventIndex;
export const NameOfIndexForRecordingUuidThenCategoryThenEventIndex = "RecordingUuid-Category-EventIndex" satisfies NameOfIndexForRecordingUuidThenCategoryThenEventIndex;


export type IndexForRecordingUuidThenCategoryThenName = {
	"RecordingUuid-Category-Name": [UUID, RecordableEventCategory, RecordableEventName, number];
}
export type NameOfIndexForRecordingUuidThenCategoryThenName = keyof IndexForRecordingUuidThenCategoryThenName;
export const NameOfIndexForRecordingUuidThenCategoryThenName = "RecordingUuid-Category-Name" satisfies NameOfIndexForRecordingUuidThenCategoryThenName;


export type IndexForRecordingUuidThenCategoryThenNameThenNumericIndex = {
	"RecordingUuid-Category-Name-Index": [UUID, RecordableEventCategory, RecordableEventName, number];
}
export type NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex = keyof IndexForRecordingUuidThenCategoryThenNameThenNumericIndex;
export const NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex = "RecordingUuid-Category-Name-Index" satisfies NameOfIndexForRecordingUuidThenCategoryThenNameThenNumericIndex;

export type IndexForRecordingUuidThenRequestIdThenCategoryThenName = {
	"RecordingUuid-RequestId-Category-Name": [UUID, string, RecordableEventCategory, RecordableEventName];
}
export type NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName = keyof IndexForRecordingUuidThenRequestIdThenCategoryThenName;
export const NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName = "RecordingUuid-RequestId-Category-Name" satisfies NameOfIndexForRecordingUuidThenRequestIdThenCategoryThenName;

export type IndexForRecordingUuidThenOriginRegisteredDomainThenDestinationRegisteredDomain = {
	"RecordingUuid-OriginRegisteredDomain-DestinationRegisteredDomain": [UUID, string, string];
}
export type NameOfIndexForRecordingUuidThenOriginRegisteredDomainThenDestinationRegisteredDomain = keyof IndexForRecordingUuidThenOriginRegisteredDomainThenDestinationRegisteredDomain;
export const NameOfIndexForRecordingUuidThenOriginRegisteredDomainThenDestinationRegisteredDomain =
	"RecordingUuid-OriginRegisteredDomain-DestinationRegisteredDomain" satisfies NameOfIndexForRecordingUuidThenOriginRegisteredDomainThenDestinationRegisteredDomain;

export type IndexForRecordingUuidThenTabIdThenTabUrl = {
	"RecordingUuid-TabId-TabUrl": [UUID, number, string];
}
export type NameOfIndexForRecordingUuidThenTabIdThenTabUrl = keyof IndexForRecordingUuidThenTabIdThenTabUrl;
export const NameOfIndexForRecordingUuidThenTabIdThenTabUrl =
	"RecordingUuid-TabId-TabUrl" satisfies NameOfIndexForRecordingUuidThenTabIdThenTabUrl;


export type IndexForReceivedEventUuidThenEventName = {
	'receivedEventUuid-eventName': [UUID, RecordableEventName, number];
}
export type NameOfIndexForReceivedEventUuidThenEventName = keyof IndexForReceivedEventUuidThenEventName;
export type TypesForIndexForReceivedEventUuidThenEventName = IndexForReceivedEventUuidThenEventName[NameOfIndexForReceivedEventUuidThenEventName];
export const NameOfIndexForReceivedEventUuidThenEventName = 'receivedEventUuid-eventName' satisfies NameOfIndexForReceivedEventUuidThenEventName;

export type IndexForEventListenerUuidThenEventName = {
	'eventListenerUuid-eventName': [UUID, RecordableEventName, number];
}
export type NameOfIndexForEventListenerUuidThenEventName = keyof IndexForEventListenerUuidThenEventName;
export type TypesForIndexForEventListenerUuidThenEventName = IndexForEventListenerUuidThenEventName[NameOfIndexForEventListenerUuidThenEventName];
export const NameOfIndexForEventListenerUuidThenEventName = 'eventListenerUuid-eventName' satisfies NameOfIndexForEventListenerUuidThenEventName;

	

export const DatabaseStoresIndexedByRecordingUuid = ["events", "videoFragments", "mhtmlCaptures"] as const;

export const getRangeForAllRecordsWithRecordingUuid = (recordingUuid: UUID) =>
	IDBKeyRange.bound([recordingUuid, 0], [recordingUuid, Number.MAX_SAFE_INTEGER]);

